import { Box, Grid, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  ChipField,
  Labeled,
  NumberField,
  RecordContextProvider,
  ReferenceField,
  SelectField,
  TextField,
} from "react-admin";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { SectionTitle } from "features/SectionTitle";
import { administrationRequests } from "api/administrationApi";
import { NationalityType } from "utils/choices";
import NestedItems from "./NestedItems";
import { useCollectionActions } from "./CollectionContext";
import ItemFormDialog from "../dialogs/ItemFormDialog";
import DeleteItemDialog from "../dialogs/DeleteItemDialog";
import CreditFields from "../forms/CreditFields";
import CyclicSubDisciplineFields from "../forms/CyclicSubDisciplineFields";

const SUB_CYCLE_FIELDS = [
  { source: "totalHours", label: "Общо часове" },
  { source: "totalLectures", label: "Общо лекции" },
  { source: "totalLabs", label: "Общо упражнения" },
  { source: "schedule.year", label: "Курс" },
  { source: "durationDays", label: "Учебни дни" },
  { source: "extracurricularActivities", label: "Извънаудиторна" },
];

const SubCycleCard = ({ cycleIndex, index, subCycles, parentDisciplineId, saving, onSave }) => {
  const subCycle = subCycles[index];

  const replaceSubCycle = next => onSave(subCycles.map((item, i) => (i === index ? next : item)));

  return (
    <RecordContextProvider value={subCycle}>
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Labeled label="Под-цикъл">
            <ReferenceField
              source="disciplineId"
              reference={administrationRequests.getDisciplinesSelect()}
              link={false}
              emptyText="-"
            >
              <ChipField
                source="name"
                emptyText="-"
                size="small"
                color="warning"
              />
            </ReferenceField>
          </Labeled>
          <SecuredComponent permission={permissions.canUpdateCurriculums}>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <ItemFormDialog
                title="Редакция · под-цикъл"
                FormFields={CyclicSubDisciplineFields}
                record={subCycle}
                formProps={{ parentDisciplineId }}
                onSave={data => replaceSubCycle({ ...subCycle, ...data })}
                triggerButton={
                  <IconButton
                    color="primary"
                    size="small"
                    aria-label="Редакция"
                    disabled={saving}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                }
              />
              <DeleteItemDialog
                title="Изтриване · под-цикъл"
                onConfirm={() => onSave(subCycles.filter((_, i) => i !== index))}
                triggerButton={
                  <IconButton
                    color="error"
                    size="small"
                    aria-label="Изтриване"
                    disabled={saving}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              />
            </Box>
          </SecuredComponent>
        </Box>

        <Grid
          container
          spacing={2}
        >
          {SUB_CYCLE_FIELDS.map(({ source, label }) => (
            <Grid
              key={source}
              item
              xs={6}
              md={2}
            >
              <Labeled label={label}>
                <NumberField
                  source={source}
                  textAlign="left"
                  emptyText="-"
                />
              </Labeled>
            </Grid>
          ))}
        </Grid>

        <NestedItems
          title="Кредити"
          singular="кредити"
          rowPrefix={`cyclicDisciplines#${cycleIndex}.disciplines#${index}.credits`}
          items={subCycle?.credits}
          FormFields={CreditFields}
          saving={saving}
          onSave={credits => replaceSubCycle({ ...subCycle, credits })}
        >
          <SelectField
            source="nationality"
            choices={NationalityType}
            label="Националност"
            sortable={false}
            emptyText="-"
          />
          <ReferenceField
            source="instructionLanguageId"
            reference={administrationRequests.getInstructionLanguagesSelect()}
            label="Език на преподаване"
            link={false}
            sortable={false}
            emptyText="-"
          >
            <TextField
              source="name"
              emptyText="-"
            />
          </ReferenceField>
          <NumberField
            source="credits"
            label="Кредити"
            sortable={false}
            textAlign="left"
            emptyText="-"
          />
        </NestedItems>
      </Box>
    </RecordContextProvider>
  );
};

// Cards rather than a grid, so each sub-cycle's credits stay in view instead of behind a second expand.
const SubCyclesSection = ({ cycleIndex }) => {
  const { items, saving, updateNestedAt } = useCollectionActions();

  const cycle = items[cycleIndex];
  const subCycles = cycle?.disciplines ?? [];

  const saveSubCycles = next => updateNestedAt(cycleIndex, "disciplines", next);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <SectionTitle
          title="Под-цикли"
          paddingY={0}
          color="secondary"
        />
        <SecuredComponent permission={permissions.canUpdateCurriculums}>
          <ItemFormDialog
            title="Добавяне · под-цикъл"
            FormFields={CyclicSubDisciplineFields}
            formProps={{ parentDisciplineId: cycle?.disciplineId }}
            onSave={data => saveSubCycles([...subCycles, data])}
            triggerButton={
              <Button
                label="Добави"
                startIcon={<AddIcon />}
                size="small"
                disabled={saving}
              />
            }
          />
        </SecuredComponent>
      </Box>

      {subCycles.length ? (
        subCycles.map((_, index) => (
          <SubCycleCard
            key={index}
            cycleIndex={cycleIndex}
            index={index}
            subCycles={subCycles}
            parentDisciplineId={cycle?.disciplineId}
            saving={saving}
            onSave={saveSubCycles}
          />
        ))
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
        >
          Няма добавени под-цикли.
        </Typography>
      )}
    </>
  );
};

export default SubCyclesSection;
